/**
 * Featured Works Dialog
 *
 * Edits the link-in-bio "Featured Works" card: its title, which artworks its strip shows and in
 * what order, where each one links, and the optional "View More" button under the strip.
 *
 * Artworks are picked from the artist's own uploads. Artworks already on a card imported from the
 * legacy app (which may not exist in AMS) are kept and can be reordered or removed like any other.
 */

import { useEffect, useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, Check, ImageOff, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import type { Artwork } from '@/types/entities'
import { useMyArtworks } from '../hooks'
import type { FeaturedArtwork, ProfileLink } from '../types'

/** How many works the strip holds; the API allows more, but a phone strip past this is a chore. */
const MAX_FEATURED = 12

/** The public, cacheable image for an artwork — the same CDN the rest of the platform serves. */
function artworkCdnUrl(fileName: string): string {
  return `https://picasso.arthaus.cloud/cache/artworks/${fileName}.webp`
}

const isHttpUrl = (u: string) => /^https?:\/\/\S+\.\S+/i.test(u.trim())

interface Item {
  key: string
  artworkId?: number
  title: string
  imageUrl: string
  linkUrl: string
}

function toItems(artworks: FeaturedArtwork[] | undefined): Item[] {
  return [...(artworks || [])]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((a, i) => ({
      key: `existing-${a.id ?? i}`,
      artworkId: typeof a.id === 'number' ? a.id : undefined,
      title: a.title || '',
      imageUrl: a.image?.url || '',
      linkUrl: a.link?.url || '',
    }))
}

export interface FeaturedWorksSubmit {
  title: string
  artworks: FeaturedArtwork[]
  action: ProfileLink['data']['action']
}

interface FeaturedWorksDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The card being edited, or null to create one. */
  link: ProfileLink | null
  /** Prefilled as each newly added work's link (typically the artist's shop link). */
  defaultLinkUrl?: string
  onSubmit: (data: FeaturedWorksSubmit) => Promise<void>
  isSubmitting: boolean
}

export function FeaturedWorksDialog({
  open,
  onOpenChange,
  link,
  defaultLinkUrl,
  onSubmit,
  isSubmitting,
}: FeaturedWorksDialogProps) {
  const { data: myArtworks, isLoading } = useMyArtworks(open)

  const [title, setTitle] = useState('Featured Works')
  const [items, setItems] = useState<Item[]>([])
  const [actionLabel, setActionLabel] = useState('')
  const [actionUrl, setActionUrl] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setTitle(link?.data?.title || 'Featured Works')
    setItems(toItems(link?.data?.artworks))
    setActionLabel(link?.data?.action?.label || '')
    setActionUrl(link?.data?.action?.link?.url || '')
    setError(null)
  }, [open, link])

  const selectedIds = useMemo(
    () => new Set(items.map((i) => i.artworkId).filter((id): id is number => id !== undefined)),
    [items],
  )

  // Artworks without a file have nothing to show on the page.
  const pickable = useMemo(
    () => (myArtworks || []).filter((a: Artwork) => a.fileName),
    [myArtworks],
  )

  const toggle = (art: Artwork) => {
    setError(null)
    if (selectedIds.has(art.id)) {
      setItems((list) => list.filter((i) => i.artworkId !== art.id))
      return
    }
    if (items.length >= MAX_FEATURED) {
      setError(`You can feature up to ${MAX_FEATURED} works.`)
      return
    }
    setItems((list) => [
      ...list,
      {
        key: `artwork-${art.id}`,
        artworkId: art.id,
        title: art.name || '',
        imageUrl: artworkCdnUrl(art.fileName as string),
        linkUrl: defaultLinkUrl || '',
      },
    ])
  }

  const move = (index: number, delta: number) => {
    setItems((list) => {
      const next = [...list]
      const target = index + delta
      if (target < 0 || target >= next.length) return list
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
  }

  const update = (index: number, patch: Partial<Item>) =>
    setItems((list) => list.map((item, i) => (i === index ? { ...item, ...patch } : item)))

  const handleSave = async () => {
    if (!title.trim()) return setError('Give the card a title.')
    if (items.length === 0) return setError('Pick at least one work to feature.')
    const badLink = items.find((i) => i.linkUrl.trim() && !isHttpUrl(i.linkUrl))
    if (badLink) return setError(`“${badLink.title || 'Untitled'}” has a link that is not a web address (https://…).`)
    if (actionUrl.trim() && !isHttpUrl(actionUrl)) return setError('The “View more” link must be a web address (https://…).')

    await onSubmit({
      title: title.trim(),
      artworks: items.map((item, position) => ({
        id: item.artworkId ?? item.key,
        position,
        title: item.title.trim(),
        image: { url: item.imageUrl, alt: item.title.trim() },
        ...(item.linkUrl.trim() ? { link: { url: item.linkUrl.trim(), target: '_blank' as const } } : {}),
      })),
      action: actionUrl.trim()
        ? { label: actionLabel.trim() || 'View More', link: { url: actionUrl.trim(), target: '_blank' } }
        : null,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[760px]">
        <DialogHeader>
          <DialogTitle>{link ? 'Edit Featured Works' : 'Add Featured Works'}</DialogTitle>
          <DialogDescription>
            Choose the pieces visitors see when they open this card, and the order they appear in.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="featured-title">Card title</Label>
            <Input
              id="featured-title"
              value={title}
              maxLength={255}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Featured Works"
            />
          </div>

          {/* Selected, in order */}
          <div className="space-y-2">
            <Label>
              Featured ({items.length}/{MAX_FEATURED})
            </Label>
            {items.length === 0 ? (
              <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
                Nothing yet. Pick works from your uploads below.
              </p>
            ) : (
              <div className="space-y-2">
                {items.map((item, index) => (
                  <div key={item.key} className="flex items-center gap-3 rounded-lg border p-2">
                    <div className="h-14 w-14 shrink-0 overflow-hidden rounded bg-muted">
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <ImageOff className="m-4 h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                    <div className="grid flex-1 gap-1.5 sm:grid-cols-2">
                      <Input
                        aria-label="Title"
                        value={item.title}
                        maxLength={255}
                        onChange={(e) => update(index, { title: e.target.value })}
                        placeholder="Title"
                      />
                      <Input
                        aria-label="Link"
                        value={item.linkUrl}
                        onChange={(e) => update(index, { linkUrl: e.target.value })}
                        placeholder="Link (optional) https://…"
                        type="url"
                      />
                    </div>
                    <div className="flex shrink-0 flex-col">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        aria-label="Move up"
                        disabled={index === 0}
                        onClick={() => move(index, -1)}
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        aria-label="Move down"
                        disabled={index === items.length - 1}
                        onClick={() => move(index, 1)}
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      aria-label="Remove"
                      onClick={() => setItems((list) => list.filter((_, i) => i !== index))}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Picker */}
          <div className="space-y-2">
            <Label>Your artworks</Label>
            {isLoading ? (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                {Array.from({ length: 10 }).map((_, i) => (
                  <Skeleton key={i} className="aspect-square w-full" />
                ))}
              </div>
            ) : pickable.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Upload some artwork first, then come back to feature it here.
              </p>
            ) : (
              <div className="grid max-h-72 grid-cols-3 gap-2 overflow-y-auto pr-1 sm:grid-cols-5">
                {pickable.map((art: Artwork) => {
                  const selected = selectedIds.has(art.id)
                  return (
                    <button
                      key={art.id}
                      type="button"
                      onClick={() => toggle(art)}
                      aria-pressed={selected}
                      title={art.name}
                      className={cn(
                        'relative aspect-square overflow-hidden rounded-md border-2 bg-muted',
                        selected ? 'border-primary' : 'border-transparent hover:border-muted-foreground/40',
                      )}
                    >
                      <img
                        src={artworkCdnUrl(art.fileName as string)}
                        alt={art.name}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                      {selected && (
                        <span className="absolute right-1 top-1 rounded-full bg-primary p-0.5 text-primary-foreground">
                          <Check className="h-3 w-3" />
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* View more */}
          <div className="space-y-2">
            <Label>“View more” button (optional)</Label>
            <div className="grid gap-2 sm:grid-cols-[1fr_2fr]">
              <Input
                value={actionLabel}
                maxLength={100}
                onChange={(e) => setActionLabel(e.target.value)}
                placeholder="View More"
              />
              <Input
                value={actionUrl}
                onChange={(e) => setActionUrl(e.target.value)}
                placeholder="https://… (leave empty to hide the button)"
                type="url"
              />
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
